/**
 * @file api.ts
 * @brief TanStack Query hooks for the Languages feature.
 *
 * @details
 * Two endpoints in scope:
 *   useLanguages          → GET /api/admin/languages
 *   useUpdateLanguageStatus → PUT /api/admin/languages/status
 *
 * The mutation does an **optimistic update** on the cached list so the
 * Switch in the row flips instantly; if the backend rejects the change
 * we roll back (and surface the error via a toast at the consumer
 * site). This mirrors the snappy feel of the mobile preferences
 * toggles in `FRONTEND/src/screens/Settings/...`.
 *
 * @dependencies
 * - @tanstack/react-query
 * - shared/api/{client,endpoints}
 *
 * @references
 * - BACKEND/src/features/admin/routes.js (GET /languages, PUT /languages/status)
 * - BACKEND/src/features/s2s/languages.mjs (catalogue + persistence)
 *
 * @author DES
 * @date 26-05-2026
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiGet, apiPut } from "@/shared/api/client";
import { adminEndpoints } from "@/shared/api/endpoints";
import {
  LanguagesResponseSchema,
  type LanguagesResponse,
  type LanguageRow,
  type UpdateLanguageStatusInput,
} from "./schemas";

export const languagesKeys = {
  all: ["admin", "languages"] as const,
  list: () => [...languagesKeys.all, "list"] as const,
};

export function useLanguages() {
  return useQuery<LanguagesResponse>({
    queryKey: languagesKeys.list(),
    queryFn: async () => {
      const raw = await apiGet<unknown>(adminEndpoints.languages);
      return LanguagesResponseSchema.parse(raw);
    },
  });
}

// ----------------------------------------------------------------------
// Optimistic toggle
// We key rows by `code|locale` (matching the backend's normalised PK)
// so multiple locales for the same code (e.g. en-US vs en-GB) don't
// step on each other.
// ----------------------------------------------------------------------
function rowKey(code: string, locale?: string | null): string {
  return `${code.toLowerCase()}|${(locale ?? "").toLowerCase()}`;
}

export function useUpdateLanguageStatus() {
  const qc = useQueryClient();

  return useMutation<
    unknown,
    Error,
    UpdateLanguageStatusInput,
    { previous?: LanguagesResponse }
  >({
    mutationFn: async (input) =>
      apiPut<unknown>(adminEndpoints.languageStatus, input),

    // Optimistic — flip the cached row immediately.
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: languagesKeys.list() });
      const previous = qc.getQueryData<LanguagesResponse>(
        languagesKeys.list(),
      );

      if (previous) {
        const targetKey = rowKey(input.code, input.locale);
        const next: LanguagesResponse = {
          languages: previous.languages.map((row): LanguageRow =>
            rowKey(row.code, row.locale) === targetKey
              ? {
                  ...row,
                  enabled: input.enabled,
                  reason: input.reason ?? row.reason ?? null,
                  updated_at: new Date().toISOString(),
                }
              : row,
          ),
        };
        qc.setQueryData(languagesKeys.list(), next);
      }
      return { previous };
    },

    // Roll back on failure.
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(languagesKeys.list(), ctx.previous);
      }
    },

    // Always re-sync from server when settled.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: languagesKeys.list() });
    },
  });
}
